import React, { useEffect, useState } from "react";
import axios from "axios";
import { Heart, Sparkles, HandHeart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FundsRaised = () => {
  const [data, setData] = useState({ amount: 700, currency: "usd", cause: "", donate_url: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/funds`)
      .then((res) => {
        if (mounted) setData(res.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (data.currency || "usd").toUpperCase(),
    maximumFractionDigits: 0,
  }).format(data.amount ?? 0);

  return (
    <section
      id="funds-raised"
      className="w-full py-16 md:py-24 px-6"
      style={{ backgroundColor: "var(--bg-primary)" }}
      data-testid="funds-raised-section"
    >
      <div className="max-w-3xl mx-auto">
        <div
          className="relative rounded-[2rem] px-8 py-12 md:px-12 md:py-16 text-center stitched-thick"
          style={{
            backgroundColor: "var(--bg-secondary)",
          }}
        >
          <Sparkles
            size={28}
            className="absolute -top-3 -left-3 rotate-12"
            style={{ color: "var(--brand-orange)" }}
          />
          <Sparkles
            size={28}
            className="absolute -bottom-3 -right-3 -rotate-12"
            style={{ color: "var(--brand-rose)" }}
          />

          <p
            className="font-script text-2xl md:text-3xl mb-3"
            style={{ color: "var(--text-accent)" }}
          >
            handmade for a cause
          </p>
          <h2
            className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mb-6"
            style={{ color: "var(--text-primary)" }}
          >
            Stitching Smiles for Kids
          </h2>

          <div
            className="inline-flex items-center justify-center gap-3 rounded-full px-6 py-3 mb-6"
            style={{
              backgroundColor: "#fff",
              border: "1px solid var(--border-soft)",
            }}
          >
            <Heart
              size={20}
              fill="currentColor"
              style={{ color: "var(--brand-rose)" }}
            />
            <span
              className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold"
              style={{ color: "var(--text-secondary)" }}
            >
              Total raised so far
            </span>
          </div>

          <div
            className="font-display text-6xl md:text-7xl lg:text-8xl leading-none"
            style={{ color: "var(--brand-sage)" }}
            data-testid="funds-raised-amount"
          >
            {loading ? "…" : formatted}
          </div>

          <p
            className="mt-6 max-w-xl mx-auto text-base md:text-lg leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
            data-testid="funds-raised-cause"
          >
            Every single plushie purchased helps us buy gifts for children at our
            local children’s hospital. 100% of profits go straight to brightening
            a little one’s day. Thank you for being part of the hug. 🧶
          </p>

          {data.donate_url && (
            <div
              className="mt-8 pt-8 max-w-md mx-auto"
              style={{ borderTop: "1px dashed var(--border-soft)" }}
              data-testid="donate-cta"
            >
              <p
                className="font-script text-xl md:text-2xl mb-3"
                style={{ color: "var(--text-accent)" }}
              >
                or… just give a little
              </p>
              <p
                className="text-sm md:text-base mb-5"
                style={{ color: "var(--text-secondary)" }}
              >
                Don’t want to purchase anything but still want to help the cause?
                Send a direct donation through PayPal — every dollar goes straight to
                the kids.
              </p>
              <a
                href={data.donate_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-sage inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold tracking-wide uppercase"
                data-testid="donate-button"
              >
                <HandHeart size={18} />
                Donate via PayPal
              </a>
              <p
                className="text-xs mt-3"
                style={{ color: "var(--text-secondary)" }}
              >
                Tip: choose <strong>“Friends &amp; Family”</strong> at the PayPal step so the full amount reaches the cause (no fees taken).
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FundsRaised;
